<template>
  <div
    class="relative w-full"
    @keydown.esc.stop.prevent="close"
  >
    <label
      v-if="label"
      :for="inputId"
      class="text-alert-closed-text mb-1 block text-xs font-semibold"
    >
      {{ label }}
    </label>

    <div
      :class="[
        'group flex items-center rounded transition-colors',
        containerBgClass,
      ]"
      @mousedown.stop
    >
      <FormField
        :id="inputId"
        ref="fieldRef"
        :value="formattedValue"
        type="tel"
        name="phoneNumber"
        :placeholder="placeholder"
        :minimal-style="true"
        :custom-padding="true"
        :remove-y-margin="true"
        :initial-country="getISO(phoneE164)"
        @input="handleInput"
        @focusin="onFocus"
        @blur="onBlur"
        @country-changed="handleCountryChange"
      />
    </div>

    <ul
      v-if="isOpen"
      :id="listId"
      class="absolute left-0 right-0 z-50 mt-1 max-h-60 overflow-y-auto rounded bg-white shadow-md"
      :class="{
        'borderGray border':
          hasResults || (query.length >= minChars && allowCreate),
      }"
      role="listbox"
    >
      <template v-if="hasResults">
        <li
          v-for="(option, index) in filteredOptions"
          :key="optionKey(option, index)"
          :class="[
            'text-info-bold-text hover:bg-alert-closed-bg cursor-pointer px-4 py-2 text-sm font-normal',
            highlightedIndex === index ? 'bg-alert-closed-bg' : '',
          ]"
          role="option"
          :aria-selected="highlightedIndex === index"
          @mousedown.prevent="selectOption(option)"
        >
          {{ formatE164WithNationalStyle(optionText(option)) }}
        </li>
      </template>

      <template v-else-if="query.length >= minChars && allowCreate">
        <li class="px-4 py-3 text-center text-sm text-steel-400">
          No matches found for
          <span class="font-normal">{{ query }}</span>
        </li>
        <li
          class="cursor-pointer px-4 pb-4 text-center text-sm font-medium text-sun-700 hover:text-sun-800"
          role="button"
          @mousedown.prevent="requestCreate"
        >
          + {{ createLabel }}
        </li>
      </template>
    </ul>
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
  defineExpose,
  defineEmits,
} from 'vue';
import FormField from '@/components/FormField.vue';
import countryIsoList from '@/utils/countryIsoList';
import parsePhoneNumber from 'libphonenumber-js';

const props = defineProps({
  label: String,
  placeholder: String,
  options: { type: Array, default: () => [] },
  modelValue: [String, Number, Object, null],
  minChars: { type: Number, default: 1 },
  allowCreate: { type: Boolean, default: true },
  createLabel: { type: String, default: 'Add Phone' },
  labelKey: { type: String, default: 'description' },
  valueKey: { type: String, default: 'value' },
  filterFn: { type: Function, default: null },
  phoneE164: { type: String, default: '' },
});
const emit = defineEmits([
  'update:modelValue',
  'selected',
  'request-create',
  'opened',
  'closed',
]);

const query = ref('');
const isFocused = ref(false);
const isOpen = ref(false);
const highlightedIndex = ref(-1);
const fieldRef = ref(null);
const inputId = `ac-${Math.random().toString(36).slice(2)}`;
const listId = `list-${inputId}`;
const localOptions = ref([]);
const initialCountry = ref({
  iso: 'us',
  dialCode: '1',
});
const isCountryDropdownOpen = ref(false);

watch(
  () => props.options,
  (v) => (localOptions.value = v || []),
  { immediate: true }
);
watch(query, (val) => {
  // Get the current country from the FormField component
  const currentCountry = fieldRef.value
    ?.getElementData()
    ?.getSelectedCountryData();
  const iso = currentCountry
    ? { iso: currentCountry.iso2, dialCode: currentCountry.dialCode }
    : initialCountry.value;

  emit('selected', {
    phone: val,
    iso: iso,
  });
});
watch(
  () => props.modelValue,
  (nv) => {
    const match = localOptions.value.find((o) => getValue(o) === nv);
    const raw = match ? optionText(match) : (nv ?? '');
    query.value = raw.toString().replace(/[^0-9]/g, '');
  },
  { immediate: true }
);

const filteredOptions = computed(() => {
  const q = normalize(query.value);
  if (!q) return localOptions.value;
  if (props.filterFn) return props.filterFn(localOptions.value, query.value);
  return localOptions.value.filter((o) => normalize(optionText(o)).includes(q));
});
const hasResults = computed(() => filteredOptions.value.length > 0);
const containerBgClass = computed(() => 'bg-steel-100 hover:bg-steel-200');

const formattedValue = computed(() => {
  const raw = query.value.replace(/\D/g, '').slice(0, 10);
  if (raw.length === 0) return '';
  if (raw.length <= 3) return raw;
  if (raw.length <= 6) return `${raw.slice(0, 3)}-${raw.slice(3)}`;
  return `${raw.slice(0, 3)}-${raw.slice(3, 6)}-${raw.slice(6)}`;
});

function getValue(o) {
  return typeof o === 'object' ? o[props.valueKey] : o;
}
function optionText(o) {
  return typeof o === 'object' ? o[props.labelKey] : String(o);
}
function optionKey(o, i) {
  return getValue(o) ?? i;
}
const normalize = (s) =>
  s
    ?.toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '') ?? '';

function handleInput(e) {
  query.value = e.target.value;
  onInput();
}
function formatE164WithNationalStyle(e164) {
  try {
    const phoneNumber = parsePhoneNumber(e164);
    return `${phoneNumber.countryCallingCode ? '+' + phoneNumber.countryCallingCode : ''} ${phoneNumber.formatNational()}`;
  } catch {
    return e164;
  }
}
function onInput() {
  query.value.length >= props.minChars ? open() : close();
  highlightedIndex.value = hasResults.value ? 0 : -1;
}

function onFocus() {
  isFocused.value = true;
  if (!isCountryDropdownOpen.value) {
    open();
  }
}
function onBlur() {
  isFocused.value = false;
  setTimeout(close, 100);
}
function selectOption(option) {
  const raw = optionText(option);
  try {
    const phone = parsePhoneNumber(raw);
    query.value = phone.formatNational();
  } catch {
    query.value = raw;
  }

  // Get the current country from the FormField component
  const currentCountry = fieldRef.value
    ?.getElementData()
    ?.getSelectedCountryData();
  const iso = currentCountry
    ? { iso: currentCountry.iso2, dialCode: currentCountry.dialCode }
    : initialCountry.value;

  emit('update:modelValue', getValue(option));
  emit('selected', {
    phone: option,
    iso: iso,
  });

  close();
  highlightedIndex.value = -1;
}
function requestCreate() {
  // Get the current country from the FormField component
  const currentCountry = fieldRef.value
    ?.getElementData()
    ?.getSelectedCountryData();
  const iso = currentCountry
    ? { iso: currentCountry.iso2, dialCode: currentCountry.dialCode }
    : initialCountry.value;

  emit('selected', {
    phone: query.value,
    iso: iso,
  });
  close();
}
function handleCountryChange(iso) {
  initialCountry.value = iso;
  // Emit the current state when country changes
  emit('selected', {
    phone: query.value,
    iso: iso,
  });
  close();
}

function open() {
  if (isCountryDropdownOpen.value) {
    return;
  }

  if (!isOpen.value) {
    isOpen.value = true;
    emit('opened');
  }
}
function close() {
  if (isOpen.value) {
    isOpen.value = false;
    highlightedIndex.value = -1;
    emit('closed');
  }
}

function addOption(option, select = true) {
  localOptions.value.push(option);
  if (select) {
    query.value = optionText(option);
    // Get the current country from the FormField component
    const currentCountry = fieldRef.value
      ?.getElementData()
      ?.getSelectedCountryData();
    const iso = currentCountry
      ? { iso: currentCountry.iso2, dialCode: currentCountry.dialCode }
      : initialCountry.value;

    emit('update:modelValue', getValue(option));
    emit('selected', {
      phone: option,
      iso: iso,
    });
  }
}
defineExpose({
  addOption,
  focus: () => fieldRef.value?.$refs?.inputField?.focus?.(),
});

function handleClick(e) {
  if (!fieldRef.value) return;

  const flagButton = e.target.closest('.iti__flag-container');
  if (flagButton) {
    close();
    return;
  }

  const countryDropdown = e.target.closest('.iti__country-list');
  if (countryDropdown) {
    close();
    return;
  }

  if (!fieldRef.value.$el.contains(e.target)) close();
}

function getISO(phoneE164) {
  const dial = phoneE164.startsWith('+') ? phoneE164 : `+${phoneE164}`;
  const sortedList = [...countryIsoList].sort(
    (a, b) => b.dial_code.length - a.dial_code.length
  );
  const match = sortedList.find((entry) => dial.startsWith(entry.dial_code));
  return match?.iso?.toLowerCase() || null;
}

function handleCountryDropdownOpen() {
  isCountryDropdownOpen.value = true;
  close();
}

function handleCountryDropdownClose() {
  isCountryDropdownOpen.value = false;
}

function checkCountryDropdownState() {
  const countryDropdown = document.querySelector('.iti__country-list');
  if (countryDropdown) {
    const isVisible =
      countryDropdown.style.display !== 'none' &&
      countryDropdown.style.visibility !== 'hidden' &&
      countryDropdown.offsetParent !== null;

    if (isVisible && !isCountryDropdownOpen.value) {
      handleCountryDropdownOpen();
    } else if (!isVisible && isCountryDropdownOpen.value) {
      handleCountryDropdownClose();
    }
  }
}

onMounted(() => {
  document.addEventListener('mousedown', handleClick);

  if (fieldRef.value?.$refs?.inputField) {
    const input = fieldRef.value.$refs.inputField;

    input.addEventListener('countrydropdownopen', handleCountryDropdownOpen);
    input.addEventListener('countrydropdownclose', handleCountryDropdownClose);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' || mutation.type === 'attributes') {
          checkCountryDropdownState();
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
    });

    setTimeout(checkCountryDropdownState, 100);
  }
});

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', handleClick);

  if (fieldRef.value?.$refs?.inputField) {
    const input = fieldRef.value.$refs.inputField;
    input.removeEventListener('countrydropdownopen', handleCountryDropdownOpen);
    input.removeEventListener(
      'countrydropdownclose',
      handleCountryDropdownClose
    );
  }
});
</script>

<style scoped>
input:focus {
  outline: none !important;
  box-shadow: none !important;
}
.borderGray {
  border-color: rgba(31, 36, 46, 0.14);
}
</style>
